import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Search, Eye, Trash2, ChevronLeft, ChevronRight, 
  Link2, ArrowRight, ArrowLeft, ArrowLeftRight, Cloud, Zap,
  ChevronUp, ChevronDown, ChevronsUpDown, Globe, FileCode,
  FolderSync, Database, Mail, Folder, Webhook, Cpu, GitBranch
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';

// Icon mapping for interface types
const interfaceTypeIcons = {
  'globe': Globe,
  'file-code': FileCode,
  'folder-sync': FolderSync,
  'database': Database,
  'mail': Mail,
  'folder': Folder,
  'webhook': Webhook,
  'cpu': Cpu,
  'git-branch': GitBranch,
  'link': Link2,
};

const AppInterfaces = () => {
  const [interfaces, setInterfaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ field: 'name', dir: 'asc' });
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ 
    interface_type_id: '', 
    criticity_id: '', 
    direction: '',
    source_app_id: '' 
  });
  
  // Options
  const [filterOptions, setFilterOptions] = useState({ 
    interface_types: [], 
    criticities: [], 
    directions: [],
    source_apps: [] 
  });
  
  // Stats
  const [stats, setStats] = useState({ total: 0, internal: 0, external: 0 });
  
  // Delete
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  const { canDelete } = usePermissions('apps', 'interfaces');

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/apps/interfaces/filter/options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/apps/interfaces/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchInterfaces = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir,
      });
      
      if (searchTerm && searchTerm.length >= 2) params.append('search', searchTerm);
      if (filters.interface_type_id) params.append('interface_type_id', filters.interface_type_id);
      if (filters.criticity_id) params.append('criticity_id', filters.criticity_id);
      if (filters.direction) params.append('direction', filters.direction);
      if (filters.source_app_id) params.append('source_app_id', filters.source_app_id);
      
      const response = await api.get(`/apps/interfaces?${params}`);
      setInterfaces(response.data.data);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        pages: response.data.pages,
      }));
    } catch (error) {
      toast.error('Error al cargar interfaces');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchInterfaces();
  }, [pagination.page, pagination.limit, sortConfig, searchTerm, filters]);

  const handleDelete = async () => {
    try {
      await api.delete(`/apps/interfaces/${deleteConfirm.id}`);
      toast.success('Interfaz eliminada');
      setDeleteConfirm(null);
      fetchInterfaces();
      fetchStats();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getDirectionIcon = (direction) => {
    switch (direction) {
      case 'Inbound': return <ArrowLeft className="h-4 w-4 text-blue-500" strokeWidth={1.5} />;
      case 'Outbound': return <ArrowRight className="h-4 w-4 text-green-500" strokeWidth={1.5} />;
      case 'Bidirectional': return <ArrowLeftRight className="h-4 w-4 text-purple-500" strokeWidth={1.5} />;
      default: return <ArrowRight className="h-4 w-4" strokeWidth={1.5} />;
    }
  };

  const getInterfaceIcon = (iconName) => {
    const IconComponent = interfaceTypeIcons[iconName] || Link2;
    return <IconComponent className="h-4 w-4" strokeWidth={1.5} />;
  };

  const getCriticityBadge = (criticity) => {
    const colors = {
      'ALTA': 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400',
      'MEDIA': 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400',
      'BAJA': 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400',
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[criticity] || 'bg-gray-100 text-gray-700'}`}>
        {criticity || 'N/A'}
      </span>
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Interfaces</h1>
            <p className="text-muted-foreground">Todas las integraciones entre aplicaciones</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="stats-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Link2 className="h-5 w-5 text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total</p>
                  <p className="text-2xl font-bold text-primary">{stats.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="stats-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Zap className="h-5 w-5 text-blue-500" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Internas</p>
                  <p className="text-2xl font-bold text-blue-500">{stats.internal}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="stats-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/10">
                  <Cloud className="h-5 w-5 text-orange-500" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Externas</p>
                  <p className="text-2xl font-bold text-orange-500">{stats.external}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="stats-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-500/10">
                  <Link2 className="h-5 w-5 text-red-500" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Críticas</p>
                  <p className="text-2xl font-bold text-red-500">
                    {stats.by_criticity?.find(c => c.name === 'ALTA')?.count || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <Input
                    placeholder="Buscar por nombre, sistema..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              
              <Select value={filters.interface_type_id || "all"} onValueChange={(v) => setFilters(p => ({ ...p, interface_type_id: v === "all" ? "" : v }))}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  {filterOptions.interface_types?.map(t => (
                    <SelectItem key={t.id} value={t.id.toString()}>{t.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filters.criticity_id || "all"} onValueChange={(v) => setFilters(p => ({ ...p, criticity_id: v === "all" ? "" : v }))}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Criticidad" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {filterOptions.criticities?.map(c => (
                    <SelectItem key={c.id} value={c.id.toString()}>{c.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filters.direction || "all"} onValueChange={(v) => setFilters(p => ({ ...p, direction: v === "all" ? "" : v }))}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Dirección" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {filterOptions.directions?.map(d => (
                    <SelectItem key={d.nombre} value={d.nombre}>{d.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filters.source_app_id || "all"} onValueChange={(v) => setFilters(p => ({ ...p, source_app_id: v === "all" ? "" : v }))}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="App Origen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las apps</SelectItem>
                  {filterOptions.source_apps?.map(a => (
                    <SelectItem key={a.id} value={a.id.toString()}>{a.code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select 
                value={pagination.limit.toString()} 
                onValueChange={(v) => setPagination(p => ({ ...p, limit: parseInt(v), page: 1 }))}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium text-xs uppercase cursor-pointer hover:bg-muted" onClick={() => handleSort('name')}>
                      <div className="flex items-center gap-1">INTERFAZ <SortIcon field="name" /></div>
                    </th>
                    <th className="text-left p-3 font-medium text-xs uppercase cursor-pointer hover:bg-muted" onClick={() => handleSort('source_app_name')}>
                      <div className="flex items-center gap-1">ORIGEN <SortIcon field="source_app_name" /></div>
                    </th>
                    <th className="text-center p-3 font-medium text-xs uppercase">DIR</th>
                    <th className="text-left p-3 font-medium text-xs uppercase">DESTINO</th>
                    <th className="text-left p-3 font-medium text-xs uppercase">TIPO</th>
                    <th className="text-left p-3 font-medium text-xs uppercase">CRITICIDAD</th>
                    <th className="text-left p-3 font-medium text-xs uppercase">FRECUENCIA</th>
                    <th className="text-right p-3 font-medium text-xs uppercase">ACCIONES</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="8" className="text-center p-8 text-muted-foreground">Cargando...</td></tr>
                  ) : interfaces.length === 0 ? (
                    <tr><td colSpan="8" className="text-center p-8 text-muted-foreground">No hay interfaces registradas</td></tr>
                  ) : (
                    interfaces.map((iface) => (
                      <tr key={iface.id} className="border-b hover:bg-muted/30 transition-colors" data-testid={`interface-row-${iface.id}`}>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="p-1.5 rounded bg-primary/10 text-primary">
                              {getInterfaceIcon(iface.interface_type_icon)}
                            </div>
                            <span className="font-medium">{iface.name}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <Link to={`/apps/${iface.source_app_id}`} className="text-primary hover:underline font-medium">
                            {iface.source_app_code}
                          </Link>
                          <div className="text-xs text-muted-foreground">{iface.source_app_name}</div>
                        </td>
                        <td className="p-3 text-center">
                          {getDirectionIcon(iface.direction)}
                        </td>
                        <td className="p-3">
                          {iface.target_app_id ? (
                            <>
                              <Link to={`/apps/${iface.target_app_id}`} className="text-primary hover:underline font-medium">
                                {iface.target_app_code}
                              </Link>
                              <div className="text-xs text-muted-foreground">{iface.target_app_name}</div>
                            </>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Cloud className="h-4 w-4 text-orange-500" strokeWidth={1.5} />
                              <div>
                                <span className="font-medium text-orange-600 dark:text-orange-400">{iface.external_system_name}</span>
                                {iface.external_vendor && (
                                  <div className="text-xs text-muted-foreground">{iface.external_vendor}</div>
                                )}
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="p-3">
                          <span className="text-sm">{iface.interface_type_name || '-'}</span>
                        </td>
                        <td className="p-3">
                          {getCriticityBadge(iface.criticity_name)}
                        </td>
                        <td className="p-3">
                          <span className="text-sm text-muted-foreground">{iface.frequency || '-'}</span>
                        </td>
                        <td className="p-3">
                          <div className="flex justify-end gap-1">
                            <Link to={`/apps/${iface.source_app_id}`}>
                              <Button variant="ghost" size="icon" className="h-8 w-8" title="Ver app origen">
                                <Eye className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            </Link>
                            {canDelete && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDeleteConfirm(iface)} title="Eliminar">
                                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between p-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Mostrando {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                    disabled={pagination.page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                  <span className="flex items-center px-3 text-sm">
                    {pagination.page} / {pagination.pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                    disabled={pagination.page === pagination.pages}
                  >
                    <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmar Eliminación</DialogTitle>
            </DialogHeader>
            <p>¿Está seguro de eliminar la interfaz <strong>{deleteConfirm?.name}</strong>?</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
              <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AppInterfaces;
